import React, { useEffect, useRef } from 'react';
import { Route, Redirect, useLocation } from 'react-router-dom';
import { OrderingSelectors } from 'polygon-ordering';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import LoyaltyWrapper from '../components/LoyaltyWrapper';
import RegistrationVerifyScreen from './RegistrationVerify';
import { setPendingRedirect } from '../slices/pendingRedirect';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { SIGN_IN_MODAL_ID } from '../modals/SignInModal';
import { useRedcatAnalytics, trackRestrictedActionBlocked, trackSigninStarted } from '../analytics';

const { getMember } = OrderingSelectors;

const ACTION_ATTEMPTED_BY_PATH: Record<string, string> = {
  '/profile': 'update_profile',
  '/change-password': 'change_password',
  '/delete-account': 'delete_account',
  '/favourites': 'manage_favourites',
  '/loyalty-history': 'view_loyalty_history',
  '/claim-missing-points': 'claim_missing_points',
  '/offers': 'view_offers',
};

const PrivateRoute: React.FC<{
  exact: boolean;
  path: string;
  component: React.ElementType;
  hideHero?: boolean;
}> = ({ component: Component, path, exact, hideHero }) => {
  const member = useAppSelector(getMember);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const currentModalId = useAppSelector(state => state.currentModalId);
  const { track } = useRedcatAnalytics();

  const currentModalIdRef = useRef<string | null>(currentModalId);
  currentModalIdRef.current = currentModalId;

  // _persist is injected by redux-persist into every persistReducer state slice.
  // Accessed via `as any` because the field is internal to redux-persist and has
  // no exported TypeScript type.
  const isOrderingRehydrated = useAppSelector(
    state => (state.ordering as any)._persist?.rehydrated,
  );

  const fullPath = location.pathname + location.search + location.hash;

  useEffect(() => {
    // Defer until ordering slice finishes rehydrating. Explicitly checks for
    // `false` so that `undefined` (no redux-persist metadata) is treated as ready.
    if (isOrderingRehydrated === false) return;

    const needsRedirect = !member || !member.verified;
    if (needsRedirect) dispatch(setPendingRedirect(fullPath));

    if (member && !member.verified) {
      trackRestrictedActionBlocked(track, {
        actionAttempted: ACTION_ATTEMPTED_BY_PATH[path] ?? path,
        blockReason: 'email_not_verified',
      });
    }

    const shouldShowSignInModal = !member && currentModalIdRef.current !== SIGN_IN_MODAL_ID;
    if (shouldShowSignInModal) {
      trackSigninStarted(track, { entryPoint: 'restricted_action' });
      dispatch(setCurrentModal({ modalId: SIGN_IN_MODAL_ID }));
    }
  }, [member?.id, member?.verified, isOrderingRehydrated, fullPath, path, track]);

  const renderContent = (props: any) => {
    if (isOrderingRehydrated === false) {
      return null;
    }

    if (!member) {
      return <Redirect to="/" />;
    }

    if (!member.verified) {
      return (
        <LoyaltyWrapper>
          <RegistrationVerifyScreen showContinueButton={false} internalRedirect />
        </LoyaltyWrapper>
      );
    }

    return (
      <LoyaltyWrapper hideHero={hideHero}>
        <Component {...props} />
      </LoyaltyWrapper>
    );
  };

  return <Route path={path} exact={exact} render={renderContent} />;
};

export default PrivateRoute;
